import React from "react";
import InlineSVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import Flex from "src/sudokit/Layout/Flex";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { Spinner } from "src/sudokit/components/Spinner";
import { GenerateResponse } from "./types";

interface ResultPanelProps {
  loading: boolean;
  imageUrl: string | null;
  result: GenerateResponse | null;
  downloadName: string;
  aspectRatio?: string;
  /** The generated subject/concept text to show above the final prompt. */
  conceptPreview?: string | null;
}

const boxStyle = (aspectRatio?: string): React.CSSProperties => ({
  aspectRatio: aspectRatio ? aspectRatio.replace(":", " / ") : "1 / 1",
});

const ResultPanel: React.FC<ResultPanelProps> = ({ loading, imageUrl, result, downloadName, aspectRatio, conceptPreview }) => {
  const effectiveAspectRatio = result?.aspectRatio ?? aspectRatio;

  const imageContent = (() => {
    if (loading) {
      return (
        <Flex align="center" justify="center" className="w-full rounded-lg bg-app-grey-4" style={boxStyle(effectiveAspectRatio)}>
          <Spinner size="lg" />
        </Flex>
      );
    }
    if (imageUrl) {
      return (
        <Flex direction="column" align="center" justify="center" gap={2} className="w-full">
          <img src={imageUrl} alt="Generated" className="block w-full rounded-lg" />
          <a
            href={imageUrl}
            download={downloadName}
            className={cn(
              "block w-full text-center rounded-xl py-3 px-4 text-sm font-medium",
              "bg-app-primary-cta text-app-cta-text-color hover:bg-app-primary-cta-hover transition-colors",
            )}
          >
            Download
          </a>
        </Flex>
      );
    }
    return (
      <Flex align="center" justify="center" className="w-full rounded-lg bg-app-grey-4" style={boxStyle(effectiveAspectRatio)}>
        <InlineSVG src="/assets/blank-avatar.svg" className="w-24 h-24" />
      </Flex>
    );
  })();

  return (
    <WithReset
      className={cn(
        "flex-shrink-0 w-full max-w-[380px] overflow-hidden p-4 rounded-xl flex flex-col gap-4",
        "bg-app-menu-color border border-app-menu-color-hover shadow-card",
        "xl:sticky lg:top-5",
      )}
    >
      {imageContent}

      {result && (conceptPreview || result.prompt) && (
        <WithReset className="flex flex-col gap-3 text-left border-t border-app-menu-color-hover pt-4">
          {(result.model || result.aspectRatio) && (
            <Text size="xs" color="grey-1">
              {[result.model, result.aspectRatio, result.titleMode].filter(Boolean).join(" · ")}
            </Text>
          )}
          {conceptPreview && (
            <div className="flex flex-col gap-1">
              <Text size="xs" weight="medium" color="grey-1">
                Generated Subject
              </Text>
              <Textarea mode="default" value={conceptPreview} readOnly minRows={4} className="w-full font-mono text-xs" />
            </div>
          )}
          {result.prompt && (
            <div className="flex flex-col gap-1">
              <Text size="xs" weight="medium" color="grey-1">
                Full Prompt Sent
              </Text>
              <Textarea mode="default" value={result.prompt} readOnly minRows={8} className="w-full font-mono text-xs" />
            </div>
          )}
        </WithReset>
      )}
    </WithReset>
  );
};

export default ResultPanel;
